/**
 * Sale-price math from purchase invoices (mirrors backend/src/services/priceCalc.js).
 * All amounts are IVA-included. No tax split in PPP / margin / sale price.
 */

export const DEFAULT_MIN_MARGIN_PCT = 30;
/** Stored % from a $1 placeholder cost (e.g. 878900) must not drive suggested price. */
export const MAX_REASONABLE_MARGIN_PCT = 200;

/** Cost of $1 (or tiny vs new invoice cost) is a placeholder, not a real PPP base. */
export function isPlaceholderCost(costoActual, costNew) {
    const old = Number(costoActual) || 0;
    const neu = Number(costNew) || 0;
    if (old <= 1) return true;
    if (neu > 0 && old > 0 && old < neu * 0.05) return true;
    return false;
}

/** Units 1–4 → down; 5–9 → up. $1.234 → $1.230; $1.237 → $1.240. */
export function roundSalePriceToTen(value) {
    const n = Math.round(Number(value) || 0);
    const units = ((n % 10) + 10) % 10;
    if (units === 0) return n;
    if (units <= 4) return n - units;
    return n + (10 - units);
}

export function computePpp(stockActual, costoActual, qtyNew, costNew) {
    const stock = Math.max(0, Number(stockActual) || 0);
    const qty = Number(qtyNew) || 0;
    const costOld = Number(costoActual) || 0;
    const costNewN = Number(costNew) || 0;
    const den = stock + qty;
    if (den <= 0) return costNewN;
    if (stock <= 0) return costNewN;
    return (stock * costOld + qty * costNewN) / den;
}

export function suggestedSalePrice(cost, pct) {
    const c = Number(cost) || 0;
    const p = Number(pct) || 0;
    return roundSalePriceToTen(c * (1 + p / 100));
}

export function profitPercent(salePrice, cost) {
    const c = Number(cost) || 0;
    const p = Number(salePrice) || 0;
    if (c <= 0) return 0;
    return (p / c - 1) * 100;
}

/** Compare at 1 decimal so rounding-to-$10 does not block a 30.0% vs 30% minimum. */
export function marginMeetsMinimum(pct, minPct) {
    const realized = Number((Number(pct) || 0).toFixed(1));
    const min = Number(minPct) || 0;
    return realized + 1e-9 >= min;
}

export function costoConIva(cost, precioTipo) {
    const c = Number(cost) || 0;
    if (String(precioTipo).toLowerCase() === 'neto') {
        return Math.round(c * 1.19 * 10000) / 10000;
    }
    return c;
}

export function validateSalePrice({ precioVenta, costo, porcentajeMinimo, enPromocion }) {
    const price = Number(precioVenta);
    const cost = Number(costo);
    if (!(cost > 0)) return 'El costo debe ser mayor a 0';
    if (!(price > 0)) return 'El precio de venta debe ser mayor a 0';
    if (price < cost) return 'El precio de venta no puede ser menor al costo';
    if (!enPromocion && price <= cost) return 'El precio de venta debe ser mayor al costo';
    const pct = profitPercent(price, cost);
    const min = Number(porcentajeMinimo);
    const minPct = Number.isFinite(min) ? min : DEFAULT_MIN_MARGIN_PCT;
    if (!enPromocion && !marginMeetsMinimum(pct, minPct)) {
        return `El % de ganancia (${Number(pct.toFixed(1)).toFixed(1)}%) es menor al mínimo configurado (${minPct}%)`;
    }
    return null;
}

export function isFirstPurchase({ previousPurchases, costoActual, porcentajeGanancia }) {
    if (Number(previousPurchases) > 0) return false;
    const cost = Number(costoActual) || 0;
    const pct = Number(porcentajeGanancia) || 0;
    return cost <= 0 || pct <= 0;
}
